import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useSyncExternalStore, useRef, useEffect } from 'react';
import { QUERIES } from '../../utils/constants';
import { clientLogger } from '../../utils/logger';
import { LanguageProvider } from './Language';
import { resolveTranslations } from './resolveTranslations';
import {
  setContextTranslations,
  type ContextTranslation,
} from './contextTranslations';

interface LanguageProviderWrapperProps {
  children: ReactNode;
}

interface RetailerSettingsData {
  retailerSettings?: {
    translation?: string;
    contextTranslations?: string;
    name?: string;
    features?: {
      isOrderless?: boolean;
      isGiftLoginEnabled?: boolean;
    };
  };
}

// Change-detection signature covering both translation payloads, so a
// context-only update still triggers a re-render (and re-hydration). JSON-array
// encoded so the two blobs can't collide (both can contain spaces).
const settingsSignature = (data?: RetailerSettingsData): string =>
  JSON.stringify([
    data?.retailerSettings?.translation ?? '',
    data?.retailerSettings?.contextTranslations ?? '',
  ]);

const parseContextTranslations = (json: string): ContextTranslation[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as ContextTranslation[]) : [];
  } catch (error) {
    clientLogger.logError(error);
    return [];
  }
};

/**
 * Wrapper component that fetches translations from retailer settings
 * and hydrates the LanguageProvider with them.
 */
export const LanguageProviderWrapper = ({
  children,
}: LanguageProviderWrapperProps) => {
  const queryClient = useQueryClient();
  const lastSignatureRef = useRef<string | null>(null);

  const data = useSyncExternalStore(
    (callback) => {
      // Subscribe to query cache changes
      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        // Only trigger re-render if retailer settings query changed
        // Check if the event is for our specific query by comparing the full queryKey
        const isRetailerSettingsQuery =
          event?.query?.queryKey &&
          Array.isArray(event.query.queryKey) &&
          event.query.queryKey.length > 0 &&
          event.query.queryKey[0] === QUERIES.GET_RETAILER_SETTINGS;

        if (!isRetailerSettingsQuery) return;

        // Compare the combined signature; fire when either payload changes.
        const current = queryClient.getQueryData<RetailerSettingsData>([
          QUERIES.GET_RETAILER_SETTINGS,
        ]);
        const currentSignature = settingsSignature(current);
        if (currentSignature !== lastSignatureRef.current) {
          lastSignatureRef.current = currentSignature;
          callback();
        }
      });
      return unsubscribe;
    },
    () => {
      const snap = queryClient.getQueryData<RetailerSettingsData>([
        QUERIES.GET_RETAILER_SETTINGS,
      ]);
      // keep ref in sync for the compare logic above
      const signature = settingsSignature(snap);
      if (signature !== lastSignatureRef.current)
        lastSignatureRef.current = signature;
      return snap;
    },
    // For SSR, prefer a deterministic snapshot to avoid hydration mismatch:
    () => ({ retailerSettings: { translation: '', contextTranslations: '' } }),
  );

  // Server dictionary layered over the static seed (throws if present but
  // unparseable — translations are a hard dependency).
  const translations = resolveTranslations(data?.retailerSettings?.translation);

  // Hydrate the context-keyed translation store (consumed via rawContextString).
  // In an effect (commit phase) rather than during render — a render-phase write
  // to the module store is unsafe under concurrent rendering (a discarded render
  // would still mutate it). The combined signature above re-renders on context
  // changes, so this re-runs whenever the payload changes. Like the flat-dict
  // global, it lands on commit; component consumers needing first-render values
  // read via context.
  const contextJSON = data?.retailerSettings?.contextTranslations ?? '';
  useEffect(() => {
    setContextTranslations(parseContextTranslations(contextJSON));
  }, [contextJSON]);

  return (
    <LanguageProvider translations={translations}>{children}</LanguageProvider>
  );
};
